import { useState } from 'react';
import type { FlightAlertRow } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Button, CustomerAvatar, IconButton, ToneChip, Tooltip, VipBadge } from '@web/ui/core';
import { calendarDayKey, formatDdMm, formatHhMm, twcn } from '@web/ui/core/utils';

import {
  departureZone,
  liveDetails,
  noticeText,
  STRIKETHROUGH_THRESHOLD_MIN,
  travelerDisplayName,
} from './alert_row_facts';

interface AlertRowProps {
  row: FlightAlertRow;
  /** Whether this row's details row is open below it. */
  expanded: boolean;
  onToggle: () => void;
  onOpenCard: () => void;
  onCreateCard: () => void;
}

/** `carrier+flightNumber` and route, e.g. `LA3220` / `GRU → REC`. */
function reasonText(row: FlightAlertRow): string {
  switch (row.reason) {
    case 'cancelled':
      return t('alerts.reason.cancelled');
    case 'diverted':
      return t('alerts.reason.diverted');
    case 'connection_missed':
      return t('alerts.reason.connection_missed');
    case 'connection_at_risk':
      return t('alerts.reason.connection_at_risk');
    case 'delayed':
      return row.live.departureDelayMinutes === null
        ? t('alerts.reason.delayedNoMinutes')
        : t('alerts.reason.delayed', { minutes: row.live.departureDelayMinutes });
    case 'schedule_changed':
      return t('alerts.reason.schedule_changed');
    default: {
      const exhaustive: never = row.reason;
      return exhaustive;
    }
  }
}

/** Scheduled/estimated departure in the DEPARTURE AIRPORT's zone (spec:
 *  "exibido no fuso do aeroporto de partida"), named in the cell's title.
 *  Strikes the scheduled time through when it differs from the estimate by
 *  ≥5 minutes.
 *
 *  `departure.timeZone` is null when the airport is unknown to cirium's
 *  IATA→IANA table (or cirium predates the field): the cell then shows the
 *  OPERATOR's zone and the title says "seu fuso" so nobody reads it as local. */
function WhenCell({ row }: Readonly<{ row: FlightAlertRow }>): React.JSX.Element {
  const { scheduledUtc, estimatedUtc, effectiveUtc, timeZone } = row.departure;
  const { zone, isAirport } = departureZone(timeZone);
  const zoneTitle = isAirport
    ? t('alerts.when.zoneTitle', { zone })
    : t('alerts.when.localZoneTitle', { zone });
  // The window spans two calendar days (−4h … +24h): a bare `06:00` is
  // ambiguous, so the day rides along whenever it is not today's — in the
  // zone the cell formats in, not the operator's. "Today" is read once, when
  // the row mounts.
  const [now] = useState(() => new Date());
  const day =
    calendarDayKey(effectiveUtc, { timeZone: zone }) !== calendarDayKey(now, { timeZone: zone })
      ? formatDdMm(effectiveUtc, { timeZone: zone })
      : null;
  // The estimate worth striking the schedule through for: null when absent or
  // within the noise threshold.
  const estimate =
    estimatedUtc !== null &&
    Math.abs(new Date(estimatedUtc).getTime() - new Date(scheduledUtc).getTime()) / 60_000 >=
      STRIKETHROUGH_THRESHOLD_MIN
      ? estimatedUtc
      : null;
  return (
    <div className="flex flex-col" title={zoneTitle}>
      {estimate !== null ? (
        <span className="text-card-id text-muted-foreground line-through">
          {formatHhMm(scheduledUtc, { timeZone: zone })}
        </span>
      ) : null}
      <span>{formatHhMm(estimate ?? effectiveUtc, { timeZone: zone })}</span>
      {day ? <span className="text-card-id text-muted-foreground">{day}</span> : null}
    </div>
  );
}

/**
 * `tel:` link styled as a button, or — when the trip carries no phone — the
 * same button turned off with the reason in a tooltip.
 *
 * `aria-disabled` rather than the native `disabled`: a disabled button is
 * neither focusable nor hoverable, which would make the reason unreachable
 * for exactly the operators who need it. There is no click handler, so the
 * control does nothing either way.
 */
function CallAction({ phone }: Readonly<{ phone: string | null }>): React.JSX.Element {
  // A blank or whitespace-only phone is no phone: `href="tel:"` dials nothing.
  const dialable = phone?.trim();
  if (!dialable) {
    return (
      <Tooltip label={t('alerts.actions.callUnavailable')}>
        <Button size="sm" variant="ghost" aria-disabled>
          {t('alerts.actions.call')}
        </Button>
      </Tooltip>
    );
  }
  return (
    <Button asChild size="sm" variant="ghost">
      <a href={`tel:${dialable}`}>{t('alerts.actions.call')}</a>
    </Button>
  );
}

/**
 * One flight-alert row: traveler, flight, when, situation, last notice,
 * card and actions. `act` rows get a red tint so the operator's eye lands
 * there first; the card link is navigation only — the drawer itself
 * handles a card the operator can't open (403).
 */
export function AlertRow({
  row,
  expanded,
  onToggle,
  onOpenCard,
  onCreateCard,
}: Readonly<AlertRowProps>): React.JSX.Element {
  const details = liveDetails(row);
  const name = travelerDisplayName(row);

  return (
    // The whole row toggles the details (a big target for a mouse); the caret
    // button is the keyboard-reachable control that carries the state.
    <tr
      aria-expanded={expanded}
      onClick={onToggle}
      className={twcn(
        'cursor-pointer border-b border-border-soft last:border-b-0',
        row.severity === 'act' && 'bg-tone-red-tint/35',
        expanded && 'border-b-0',
      )}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <IconButton
            icon={expanded ? 'caret-down' : 'caret-right'}
            variant="ghost"
            size="xs"
            aria-expanded={expanded}
            aria-label={t(expanded ? 'alerts.row.collapse' : 'alerts.row.expand', { name })}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
          <CustomerAvatar name={name} vip={row.traveler.vip} size="sm" />
          <div className="flex flex-col">
            <span className="flex items-center gap-1.5 text-meta font-medium text-foreground">
              {name}
              {row.traveler.vip ? <VipBadge /> : null}
            </span>
            {row.traveler.companyName ? (
              <span className="text-card-id text-muted-foreground">{row.traveler.companyName}</span>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-meta text-foreground">
        <span className="block font-mono text-card-id">
          {row.flight.carrier}
          {row.flight.flightNumber}
        </span>
        <span className="block text-card-id text-muted-foreground">
          {row.flight.departureAirport} → {row.flight.arrivalAirport}
        </span>
      </td>
      <td className="px-3 py-2 text-meta text-foreground">
        <WhenCell row={row} />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1">
          <ToneChip tone={row.severity === 'act' ? 'red' : 'amber'}>{reasonText(row)}</ToneChip>
          {details ? <span className="text-card-id text-muted-foreground">{details}</span> : null}
        </div>
      </td>
      <td className="px-3 py-2">
        <span
          className={twcn(
            'text-meta',
            row.notice.kind === 'notified' ? 'text-tone-green-fg' : 'text-muted-foreground',
          )}
        >
          {noticeText(row)}
        </span>
      </td>
      <td className="px-3 py-2">
        {row.card ? (
          <div className="flex flex-col text-meta text-foreground">
            <span>{t('alerts.card.open', { seq: row.card.seq })}</span>
            {row.card.assigneeName ? (
              <span className="text-card-id text-muted-foreground">{row.card.assigneeName}</span>
            ) : null}
          </div>
        ) : (
          <span className="text-meta text-muted-foreground">{t('alerts.card.none')}</span>
        )}
      </td>
      {/* Actions never toggle the row: a click here is the action, not curiosity. */}
      <td
        className="px-3 py-2"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex items-center gap-2">
          {row.card ? (
            <Button size="sm" onClick={onOpenCard}>
              {t('alerts.actions.openCard')}
            </Button>
          ) : (
            <Button size="sm" onClick={onCreateCard}>
              {t('alerts.actions.createCard')}
            </Button>
          )}
          <CallAction phone={row.traveler.phone} />
        </div>
      </td>
    </tr>
  );
}
